import cx from "classnames"
import styled from "react-emotion"
import PropTypes from "prop-types"
import React, { Component, Fragment } from "react"
import BlockDebugPortal from "./BlockDebugPortal"
import { BlockContextConsumer, block } from "./ContextProviders"
import {
    DateBlock,
    DefineEntityBlock,
    DeviceSelectorBlock,
    TextBlock,
    UserBlock,
    UsersBlock,
} from "./DebugBlock"
import BlockActionsPortal from "./actions/BlockActionsPortal"
import BlockAttributesPortal from "./attributes/BlockAttributesPortal"
import BlockStylesPortal from "./styles/BlockStylesPortal"
import GetCurrentBlockValidation from "./block/GetCurrentBlockValidation"
import { getBlockByType } from "./blockTypes"
import CardBlockType from "./blockTypes/entity/CardBlock"
import Draggable from "./dnd/Draggable"
import Droppable from "./dnd/Droppable"
import BlockPropertiesPortal from "./properties/BlockPropertiesPortal"
import BlockValidationPortal from "./validation/BlockValidationsPortal"
import DisplayBlockValidation from "./block/DisplayBlockValidation"
import BehaviorPortal from "./block/BehaviorPortal"
import BlockVisibility from "./BlockVisibility"
import ModalBlockType from "./blockTypes/layout/ModalBlock"
import { evaluateFormulas, evaluateStyleFormulas } from "./formulas/evaluate"

import { hotkeys } from "react-keyboard-shortcuts"

const outerProperties = [
    "height",
    "width",
    "alignSelf",
    "layout",
    "margin",
    "marginLeft",
    "marginRight",
    "marginTop",
    "marginBottom",
    "display",
    "maxHeight",
    "minHeight",
    "maxWidth",
    "minWidth",
    "overflow",
    "boxShadow",
]

function getOuterStyle(userStyle, configStyle, viewport, direction) {
    // These styles should only apply to a block's surrounding div
    let style
    if (configStyle.default && configStyle[viewport]) {
        style = { ...configStyle.default, ...configStyle[viewport] }
    } else if (configStyle.default) {
        style = configStyle.default
    } else if (configStyle[viewport]) {
        style = configStyle[viewport]
    }

    if (userStyle && style) {
        style = { ...style, ...userStyle }
    } else if (userStyle) {
        style = userStyle
    }

    // Apply the "extra" styles
    if (style && style.extras) style = { ...style, ...style.extras }

    // We should only be keeping references to e.g. position, size, z-index etc.
    const permittedProperties = outerProperties
    const filteredStyles = Object.keys(style || {})
        .filter(key => permittedProperties.includes(key))
        .reduce((obj, key) => {
            obj[key] = style[key]
            return obj
        }, {})

    // Adjust the styles based on our custom helper attributes

    if (filteredStyles.layout === "clear") {
        filteredStyles.width = "100%"
    } else if (filteredStyles.layout === "spread") {
        filteredStyles.flexGrow = 1
    } else {
        // if (filteredStyles.layout === "tight") {
        filteredStyles.flexGrow = 0
    }

    ;[
        "marginLeft",
        "marginRight",
        "marginTop",
        "marginBottom",
        "width",
        "minWidth",
        "maxWidth",
        "height",
        "minHeight",
        "maxHeight",
    ].forEach(property => {
        let val = filteredStyles[property]

        if (parseInt(val).toString() === val) {
            filteredStyles[property] = val + "px"
        }
    })

    return filteredStyles
}

function getInnerStyle(userStyle, configStyle, viewport, direction) {
    let style
    if (configStyle.default && configStyle[viewport]) {
        style = { ...configStyle.default, ...configStyle[viewport] }
    } else if (configStyle.default) {
        style = configStyle.default
    } else if (configStyle[viewport]) {
        style = configStyle[viewport]
    }

    if (userStyle && style) {
        style = { ...style, ...userStyle }
    } else if (userStyle) {
        style = userStyle
    }

    // Apply the "extra" styles
    if (style && style.extras) style = { ...style, ...style.extras }

    const forbiddenProperties = outerProperties
    const filteredStyles = Object.keys(style || {})
        .filter(key => !forbiddenProperties.includes(key))
        .reduce((obj, key) => {
            obj[key] = style[key]
            return obj
        }, {})

    if (filteredStyles.flexDirection) {
        direction = filteredStyles.flexDirection
    }

    // if (direction === "row") {
    //     filteredStyles.flexBasis = filteredStyles.width
    //     filteredStyles.width = undefined
    // }

    // console.log(`Direction: ${direction}, HA: ${filteredStyles.horizontalAlignment}`)
    if (filteredStyles.horizontalAlignment) {
        filteredStyles[direction === "column" ? "alignItems" : "justifyContent"] =
            filteredStyles.horizontalAlignment
    }

    if (filteredStyles.verticalAlignment) {
        filteredStyles[direction === "column" ? "justifyContent" : "alignItems"] =
            filteredStyles.verticalAlignment
    }

    // Adjust the styles based on our custom helper attributes
    if (filteredStyles.horizontalSpacing || filteredStyles.horizontalAlignment) {
        filteredStyles[direction === "column" ? "alignContent" : ""] =
            filteredStyles.horizontalSpacing || filteredStyles.horizontalAlignment
    }

    if (filteredStyles.verticalSpacing || filteredStyles.verticalAlignment) {
        filteredStyles[direction === "column" ? "justifyContent" : "alignContent"] =
            filteredStyles.verticalSpacing || filteredStyles.verticalAlignment
    }

    ;["paddingLeft", "paddingRight", "paddingTop", "paddingBottom"].forEach(property => {
        let val = filteredStyles[property]

        if (parseInt(val).toString() === val) {
            filteredStyles[property] = val + "px"
        }
    })

    return filteredStyles
}

/**
 * @augments {Component<BlockProps, any>}
 */
class Block extends Component {
    state = {
        properties: null,
    }
    /**
     * @param {BlockContext} context
     * @return {BlockTypeUtils}
     */
    getUtils = (context, BlockType) => {
        const utils = {
            // Root Props get passed to the Wrapper
            getRootProps: (props = {}) => ({
                ...props,
                ...(this.props.getRootProps && this.props.getRootProps(props)),
                onClick: $event => {
                    if (context.editor.isEditing) {
                        if (!this.props.element.config.preventSelection)
                            context.actions.selectBlock(this.props.element.id)
                        $event.preventDefault()
                        $event.stopPropagation()
                    }
                    if (BlockType === ModalBlockType) {
                        $event.stopPropagation()
                    }
                },
                onMouseOver: $event => {
                    if (context.editor.isEditing) {
                        if (!this.props.element.config.preventSelection)
                            context.actions.onHover(this.props.element.id)
                        return $event.stopPropagation()
                    }
                },

                className: cx(
                    props.className,
                    "block",
                    this.props.element.type + "-block",
                    "block-" + this.props.element.id,
                    {
                        isSelected: context.editor.selected === this.props.element.id,
                        isDragging: this.props.dnd && this.props.dnd.isDragging,
                        column: (props.direction || context.direction) === "column",
                    }
                ),
                style: props.noStyles
                    ? {}
                    : evaluateStyleFormulas(
                          context,
                          getOuterStyle(
                              props.style,
                              this.props.element.config.style,
                              context.deviceType,
                              props.direction || context.direction
                          )
                      ),
                innerstyle: getInnerStyle(
                    props.style,
                    this.props.element.config.style,
                    context.deviceType,
                    props.direction || context.direction
                ),
                selected: context.editor.selected === this.props.element.id,
                childIsSelected: context.editor.selectedParent === this.props.element.id,
            }),
            renderChildren(childBlocks = []) {
                if (context.editor.isEditing) {
                    return childBlocks.map(this.renderChild)
                }

                return childBlocks.map((child, position) => this.renderChild(child, position))
            },
            renderChild(child, position) {
                return (
                    <Draggable
                        key={child.id || position}
                        payload={{
                            id: child.id,
                            position,
                        }}
                        position={position}
                        onDrop={context.editor.onDrop}
                        children={({ getRootProps, dnd }) => (
                            <Block element={child} getRootProps={getRootProps} dnd={dnd} />
                        )}
                    />
                )
            },
            selected: context.editor.selected === this.props.element.id,
            updateAttributes: (key, value) => {
                context.actions.updateAttributes(this.props.element.id, key, value)
            },
            updateStyle: (key, value) => {
                context.actions.updateStyle(this.props.element.id, key, value)
            },
        }
        return utils
    }

    render() {
        const { type, id, childBlocks, config } = this.props.element
        // Question: If we have getBlockByType, why do we need this?
        // MSKELLY
        // Answer: old prototype shit, also you can remove this comment
        // LMARGIRIER
        let BlockType = getBlockByType(type).blockElement

        return (
            <BlockContextConsumer>
                {context => (
                    <HotkeyWrapper
                        active={context.editor.isEditing}
                        deselect={context.actions.selectBlock}
                        delete={
                            context.editor.selected === id
                                ? () => context.actions.removeBlock(context.block.id)
                                : () => {}
                        }
                    >
                        <block.Provider value={this.props.element}>
                            <GetCurrentBlockValidation>
                                {validation => (
                                    <Fragment>
                                        <BlockVisibility block={this.props.element}>
                                            <BlockType
                                                id={id}
                                                type={type}
                                                config={config}
                                                context={context}
                                                childBlocks={childBlocks}
                                                Wrapper={BlockTypeWrapper}
                                                utils={this.getUtils(context, BlockType)}
                                                validation={validation}
                                                renderDroppable={(
                                                    direction = context.direction || "column",
                                                    position
                                                ) => (
                                                    <Droppable
                                                        dimensional={direction}
                                                        payload={{
                                                            id,
                                                            position: position,
                                                            direction: direction,
                                                        }}
                                                        onDrop={console.log}
                                                    >
                                                        {({ getRootProps }) => (
                                                            <div
                                                                {...getRootProps({
                                                                    className: "droppable-multid",
                                                                })}
                                                            />
                                                        )}
                                                    </Droppable>
                                                )}
                                            />
                                            {context.editor.selected === id && (
                                                <Fragment>
                                                    <BlockStylesPortal />
                                                    <BlockAttributesPortal />
                                                    <BlockPropertiesPortal />
                                                    <BlockActionsPortal /> {/* emitted actions */}
                                                    <BehaviorPortal /> {/* Remove, move … */}
                                                    <BlockValidationPortal />
                                                    <BlockDebugPortal />
                                                </Fragment>
                                            )}
                                        </BlockVisibility>
                                    </Fragment>
                                )}
                            </GetCurrentBlockValidation>
                        </block.Provider>
                    </HotkeyWrapper>
                )}
            </BlockContextConsumer>
        )
    }
}

/**
 * @augments {Component<{children: BlockTypeWrapperChildrenType, style?: any, className?: string} & BlockTypeProps,any>}
 */
class BlockTypeWrapper extends Component {
    getStateAndProps = () => ({
        attrs: this.props.config.attributes,
        computedStyle: evaluateStyleFormulas(
            this.props.context,
            getInnerStyle(this.props.style, this.props.config.style, this.props.context.deviceType)
        ),
        selected: this.props.selected,
        updateAttributes: this.props.updateAttributes,
        evaluateFormulas: evaluateFormulas(this.props.context),
        DropBox: () => {
            return (
                <div className="display-flex flex-column position-relative drop-here">
                    {!this.props.childBlocks || this.props.childBlocks.length === 0
                        ? this.props.renderDroppable(null)
                        : this.props.utils.renderChildren(this.props.childBlocks)}
                </div>
            )
        },
        ...this.props.utils,
        runOnClick: availableActions => {
            const context = this.props.context
            const actions = this.props.config.attributes.actions

            const promiseList = actions.map(action => {
                if (action.type == "page.action.redirect") {
                    const location = evaluateFormulas(this.props.context)(action.location)
                    return () => new Promise(resolve => resolve((window.location = location)))
                } else {
                    const matching = (availableActions || []).filter(
                        available => available.id === action.type
                    )
                    if (matching.length) {
                        const action = matching[0].action
                        return action
                    } else return () => Promise.resolve()
                }
            })

            evaluatePromiseSerial(promiseList)
        },
    })
    render() {
        return (
            <div
                {...this.props.utils.getRootProps({
                    noStyles: this.props.noStyles,
                    style: this.props.style,
                    className: cx(this.props.className, {
                        hasErrors: this.props.validation.errors.length,
                        hasWarnings: this.props.validation.warnings.length,
                    }),
                })}
                key={this.props.id}
                id={this.props.id}
            >
                {this.props.context.editor.isEditing && (
                    <DisplayBlockValidation validation={this.props.validation} />
                )}
                <BlockName className="block-name">
                    {this.props.config.attributes.label || this.props.id}
                </BlockName>
                <Droppable
                    dimensional={this.props.context.direction}
                    payload={{
                        id: this.props.id,
                        position: null,
                        direction: this.props.context.direction,
                    }}
                    onDrop={console.log}
                >
                    {({ getRootProps }) => (
                        <div {...getRootProps({ className: "droppable-multid" })} />
                    )}
                </Droppable>
                {this.props.children(this.getStateAndProps())}
            </div>
        )
    }
}

class UnWrappedHotkeyWrapper extends React.PureComponent {
    hot_keys = this.props.active && {
        esc: {
            // combo from mousetrap
            priority: 1,
            handler: event => this.props.deselect(),
        },
        // del: {
        //     // Need to somehow avoid deletes from in focussed checkboxes, so let's pass on this for now
        //     priority: 1,
        //     handler: event => this.props.delete(),
        // },
    }

    render() {
        return this.props.children
    }
}

const HotkeyWrapper = hotkeys(UnWrappedHotkeyWrapper)

Block.propTypes = {
    dndconnect: PropTypes.func,
    element: PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        config: PropTypes.shape({
            attributes: PropTypes.object,
            style: PropTypes.object,
        }),
    }).isRequired,
}

export default Block

export const BlockName = styled("div")`
    background: orange;
    position: absolute;
    bottom: 100%;
    color: white;
    font-weight: bold;
    font-family: Avenir-Next;
    font-size: 12px;
    padding: 1px 5px;
    display: none;
`

const evaluatePromiseSerial = funcs =>
    funcs.reduce(
        (promise, func) => promise.then(result => func().then(Array.prototype.concat.bind(result))),
        Promise.resolve([])
    )



// WEBPACK FOOTER //
// ./src/features/editor/Block.js